import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error('Authentication failed');
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const result = await exchangeSession(sessionId);
        
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        toast.success('Welcome to LusoLove!');
        
        // Redirect based on user state
        if (!result.user.is_email_verified) {
          navigate('/verify-email', { replace: true, state: { user: result.user } });
        } else if (!result.user.onboarding_complete) {
          navigate('/onboarding', { replace: true, state: { user: result.user } });
        } else {
          navigate('/discover', { replace: true, state: { user: result.user } });
        }
      } catch (error) {
        toast.error('Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Heart className="w-10 h-10 text-[#6E1A24]" strokeWidth={1.5} />
          <span className="text-3xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
        </div>
        
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-[#6E1A24] animate-spin" />
          <span className="body-text">Completing sign in...</span>
        </div>
      </div>
    </div>
  );
}
